/**
 * Help-assistant chat context object, types and hook (D10-UI-003, #3604).
 *
 * Split out of SupportChatContext.tsx so that module exports only the provider
 * (React Fast Refresh, same pattern as the tour/auth contexts). The provider is
 * mounted once above the routes, so the conversation survives every client-side
 * route change -- including a switch between the /crm and /admin shells.
 */
import { createContext, useContext } from "react";
import type { OfflineReason, SupportAction, SupportStatus } from "../api/support";

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  /** Assistant messages only: streaming → done, or error. */
  status: "streaming" | "done" | "error";
  truncated?: boolean;
  /**
   * A validated action that arrived with this reply (D10-ACT, #3605). It runs
   * once the reply completes; the message keeps it so the user can run it again.
   */
  action?: SupportAction;
  /** Why the action could not run here (e.g. a tour this user cannot see). */
  actionNote?: string;
}

export interface SendOptions {
  route: string;
  allowedRoutes: string[];
  allowedTours: string[];
}

export interface SupportActionContext {
  /** The conversation the action came from (for the feedback excerpt). */
  conversationId: number | null;
  /**
   * True once "New chat" (or a change of user) has replaced the conversation
   * the action came from (#3702). A handler checks it after any wait and does
   * nothing further -- an old reply's action must not run in a fresh chat.
   */
  isStale?: () => boolean;
}

/**
 * Carries out a validated action. Resolves to a short note when it could not
 * run (shown under the reply), or nothing when it ran.
 */
export type SupportActionHandler = (
  action: SupportAction,
  ctx: SupportActionContext,
) => void | string | Promise<void | string>;

export interface SupportChatValue {
  /** Null until the first status fetch settles. */
  status: SupportStatus | null;
  /** Fetch the status once per session (idempotent). */
  ensureStatus: () => void;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  /**
   * Text waiting to be put in the panel's input, unsent (#4601: a help-nudge
   * tip opens the panel with its question ready to send). The panel takes it
   * once and calls `clearPrefill`.
   */
  prefill: string | null;
  /** Open the panel with `text` in the input (never sent for the user). */
  openWithPrefill: (text: string) => void;
  clearPrefill: () => void;
  messages: ChatMessage[];
  conversationId: number | null;
  /** True while waiting for a reply's first token. */
  awaitingFirstToken: boolean;
  /** True while a reply is streaming. */
  streaming: boolean;
  /** Non-null when the panel should show offline help instead of chat. */
  offline: { reason: OfflineReason | "status" } | null;
  /** Go back to chat after a transient offline answer (e.g. every slot busy). */
  retryChat: () => void;
  send: (text: string, opts: SendOptions) => Promise<void>;
  /** Clear the conversation; the next message starts a new one. */
  newChat: () => void;
  /**
   * Register the handler validated stream actions are passed to (#3605). An
   * action runs after its reply's `done`, and only while the panel is open.
   */
  setActionHandler: (handler: SupportActionHandler | null) => void;
  /** Run a reply's action again (its chip in the conversation). */
  runMessageAction: (messageId: string) => void;
}

const NOOP: SupportChatValue = {
  status: null,
  ensureStatus: () => {},
  isOpen: false,
  setOpen: () => {},
  prefill: null,
  openWithPrefill: () => {},
  clearPrefill: () => {},
  messages: [],
  conversationId: null,
  awaitingFirstToken: false,
  streaming: false,
  offline: null,
  retryChat: () => {},
  send: async () => {},
  newChat: () => {},
  setActionHandler: () => {},
  runMessageAction: () => {},
};

export const SupportChatContext = createContext<SupportChatValue>(NOOP);

export function useSupportChat(): SupportChatValue {
  return useContext(SupportChatContext);
}
